/**
 * FX-FE-001 — Shared Financial Operating System config adapter.
 *
 * Single source of truth for the Four-Layer Currency Model on the frontend so
 * Settings and Finance Studio consume identical canonical response shapes and
 * cannot drift. Read-only fetch (except the L1 save helper). No conversion.
 *
 * Layers:
 *   L1 Operating Currency        GET/PUT /settings/operating-currency  (canonical: operating_currency)
 *   L2 Engine Canonical Currency GET     /settings/financial-engine-canonical-currency (read-only USD)
 *   L3 Settlement Currency       (gateway) — no authoritative endpoint yet → truthful unavailable
 *   L4 Exchange Rate Policy      GET     /settings/exchange-rate-policy  (read-only summary)
 *
 * Never reads preferred_currency or base_currency as canonical L1 state.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "./api";

/** Map raw provenance enums → owner-facing keys (localized by consumers). */
export function provenanceKey(prov) {
  switch (prov) {
    case "tenant_configured": return "owner_configured";
    case "defaulted": return "fallback_in_use";
    case "legacy_global_fallback": return "fallback_in_use";
    case "missing": return "fallback_in_use";
    default: return null;
  }
}

export function useFinancialConfig() {
  const [operating, setOperating] = useState({ data: null, loading: true, error: false });
  const [engine, setEngine] = useState({ data: null, loading: true, error: false });
  const [policy, setPolicy] = useState({ data: null, loading: true, error: false });
  const [settlement, setSettlement] = useState({ data: null, loading: true, error: false });

  const loadOperating = useCallback(async () => {
    setOperating((s) => ({ ...s, loading: true, error: false }));
    try {
      const r = await api.get("/settings/operating-currency");
      setOperating({ data: r.data, loading: false, error: false });
    } catch {
      setOperating({ data: null, loading: false, error: true });
    }
  }, []);

  const loadPolicy = useCallback(async () => {
    setPolicy((s) => ({ ...s, loading: true, error: false }));
    try {
      const r = await api.get("/settings/exchange-rate-policy");
      setPolicy({ data: r.data, loading: false, error: false });
    } catch { setPolicy({ data: null, loading: false, error: true }); }
  }, []);

  const loadSettlement = useCallback(async () => {
    setSettlement((s) => ({ ...s, loading: true, error: false }));
    try {
      const r = await api.get("/tenant-stripe/status");
      setSettlement({ data: r.data, loading: false, error: false });
    } catch { setSettlement({ data: null, loading: false, error: true }); }
  }, []);

  const load = useCallback(async () => {
    loadOperating();
    loadPolicy();
    loadSettlement();
    try {
      const r = await api.get("/settings/financial-engine-canonical-currency");
      setEngine({ data: r.data, loading: false, error: false });
    } catch { setEngine({ data: null, loading: false, error: true }); }
  }, [loadOperating, loadPolicy, loadSettlement]);

  useEffect(() => { load(); }, [load]);

  /** Owner save for L1. Explicit action only — never auto-writes on selection. */
  const saveOperatingCurrency = useCallback(async (code) => {
    const res = await api.put("/settings/operating-currency", { operating_currency: code });
    await loadOperating();
    return res.data;
  }, [loadOperating]);

  /** L4 — save manual reference rates (activates manual override). Explicit owner action. */
  const saveManualRates = useCallback(async ({ usd_to_mxn, usd_to_eur }) => {
    const res = await api.put("/settings/exchange-rate", { usd_to_mxn, usd_to_eur });
    await loadPolicy();
    return res.data;
  }, [loadPolicy]);

  /** L4 — fetch a live reference rate (used both to refresh and to switch back to Automatic). */
  const fetchLiveRate = useCallback(async () => {
    const res = await api.post("/settings/exchange-rate/fetch-live");
    await loadPolicy();
    return res.data;
  }, [loadPolicy]);

  // L3 settlement is gateway-derived only; never inferred from L1/L2.
  const settlementView = (() => {
    const d = settlement.data;
    if (settlement.loading) return { loading: true };
    if (!d || settlement.error) return { available: false, reason: "unavailable" };
    if (!d.connected && d.status === "not_started") return { available: false, reason: "not_connected", gateway: d.gateway };
    if (d.status === "stale") return { available: false, reason: "stale", gateway: d.gateway };
    if (!d.settlement_currency) return { available: false, reason: "no_currency", gateway: d.gateway };
    return {
      available: true, currency: d.settlement_currency, gateway: d.gateway || "stripe",
      country: d.account_country, charges_enabled: d.charges_enabled,
      payouts_enabled: d.payouts_enabled, refreshed_at: d.refreshed_at,
    };
  })();

  return { operating, engine, policy, settlement: settlementView, reload: load,
           saveOperatingCurrency, saveManualRates, fetchLiveRate };
}

